import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Skeleton, Stack } from '@mui/material';
import { PauseCircleOutline as PausedIcon } from '@mui/icons-material';
import { jobSourcesApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { BoardReadResult, IngestSettings, JobSourcesOverview, SourceBoard } from '../../types';
import { errorMessage } from '../../utils/errors';
import { atsLabel } from '../../utils/jobBoard';
import { BoardListState, DEFAULT_BOARD_LIST, boardListSearch, countLabel, parseBoardListQuery } from '../../utils/jobSources';
import AddBoardDialog from '../../components/jobSources/AddBoardDialog';
import BoardsSection from '../../components/jobSources/BoardsSection';
import RecentRuns from '../../components/jobSources/RecentRuns';
import RefreshNow from '../../components/jobSources/RefreshNow';
import SourcesSettings from '../../components/jobSources/SourcesSettings';
import SourcesStatus from '../../components/jobSources/SourcesStatus';
import SectionTabs, { SectionTab } from '../../components/admin/SectionTabs';

const TABS: SectionTab[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'boards', label: 'Boards' },
  { value: 'settings', label: 'Settings' },
];

/**
 * The Jobs section of the staff area, about the job board's sources. Three
 * tabs: Overview (what is fresh, stale or failing, refreshing by hand, recent
 * runs), Boards (which boards are read) and Settings (how often). Every
 * action re-reads the overview, so the numbers follow without a page reload.
 */
const JobSourcesPage: React.FC = () => {
  const { show, toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const tab = TABS.some((t) => t.value === tabParam) ? (tabParam as string) : 'overview';

  const [overview, setOverview] = useState<JobSourcesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [boardsKey, setBoardsKey] = useState(0);
  const [pausing, setPausing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Only the newest answer is kept, so overlapping reloads cannot go backwards.
  const requestId = useRef(0);
  const loadOverview = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const data = await jobSourcesApi.overview();
      if (id !== requestId.current) return;
      setOverview(data);
      setError(null);
    } catch (e) {
      if (id !== requestId.current) return;
      setError(errorMessage(e, 'Could not load the job sources. Please try again.'));
    }
  }, []);

  useEffect(() => {
    loadOverview();
    return () => {
      // Drop an answer that arrives after the page is left.
      requestId.current += 1;
    };
  }, [loadOverview]);

  /** A board changed in the table, which already shows it: only the numbers are stale. */
  const refreshNumbers = useCallback(() => {
    loadOverview();
  }, [loadOverview]);

  /** Something changed that the table cannot know about: re-read both. */
  const refreshAll = useCallback(() => {
    loadOverview();
    setBoardsKey((key) => key + 1);
  }, [loadOverview]);

  const applySettings = useCallback(
    (settings: IngestSettings) => {
      setOverview((prev) => (prev ? { ...prev, settings } : prev));
      // What is due and which ATS are read both follow from the settings.
      refreshAll();
    },
    [refreshAll]
  );

  const setPaused = useCallback(
    async (paused: boolean) => {
      setPausing(true);
      try {
        const settings = await jobSourcesApi.patchSettings({ paused });
        applySettings(settings);
        show(paused ? 'Scheduled refreshing paused.' : 'Scheduled refreshing resumed.', paused ? 'warning' : 'success');
      } catch (e) {
        show(errorMessage(e, paused ? 'Could not pause refreshing.' : 'Could not resume refreshing.'), 'error');
      } finally {
        setPausing(false);
      }
    },
    [applySettings, show]
  );

  const showBoards = useCallback(
    (filter: Partial<Pick<BoardListState, 'state' | 'agency'>>) => {
      const current = parseBoardListQuery(location.search);
      navigate({ pathname: '/admin/jobs/boards', search: boardListSearch({ ...DEFAULT_BOARD_LIST, sort: current.sort, ...filter }) });
    },
    [location.search, navigate]
  );

  const handleAdded = useCallback(
    (board: SourceBoard, result: BoardReadResult | null) => {
      setAddOpen(false);
      const name = board.company_name || board.board_token;
      const where = atsLabel(board.platform);
      if (result?.error) {
        show(`${name} (${where}) was added, but the first read failed: ${result.error}`, 'warning');
      } else if (result) {
        show(`${name} (${where}) added with ${countLabel(result.active, 'listing')}.`);
      } else {
        show(`${name} (${where}) added. It will be read on the next refresh.`);
      }
      refreshAll();
    },
    [refreshAll, show]
  );

  const paused = overview?.settings.paused === true;

  return (
    <Box>
      <SectionTabs label="Jobs" tabs={TABS} value={tab} to={(value) => `/admin/jobs/${value}`} dense />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadOverview}>
              Retry
            </Button>
          }
        >
          {overview ? `The numbers below may be out of date. ${error}` : error}
        </Alert>
      )}

      {paused && (
        <Alert
          severity="warning"
          icon={<PausedIcon />}
          sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" variant="outlined" disabled={pausing} onClick={() => setPaused(false)}>
              {pausing ? <CircularProgress size={16} color="inherit" aria-label="Resuming" /> : 'Resume'}
            </Button>
          }
        >
          <strong>Scheduled refreshing is paused.</strong> Boards are only read when you refresh them by hand, so the job
          board goes stale until it is resumed.
        </Alert>
      )}

      {!overview ? (
        !error && (
          <Stack spacing={1.5} aria-busy="true" aria-label="Loading job sources">
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={180} />
            <Skeleton variant="rounded" height={260} />
          </Stack>
        )
      ) : (
        <Stack spacing={2}>
          {tab === 'overview' && (
            <>
              <SourcesStatus overview={overview} onShowBoards={showBoards} />
              <RefreshNow overview={overview} onProgress={refreshAll} />
              <RecentRuns runs={overview.recent_runs ?? []} />
            </>
          )}
          {tab === 'boards' && (
            <BoardsSection
              reloadKey={boardsKey}
              globalHours={overview.settings.refresh_interval_hours}
              onChanged={refreshNumbers}
              onAdd={() => setAddOpen(true)}
              notify={show}
            />
          )}
          {tab === 'settings' && (
            <Box sx={{ maxWidth: 720 }}>
              <SourcesSettings
                settings={overview.settings}
                pausing={pausing}
                onSetPaused={setPaused}
                onSaved={applySettings}
                notify={show}
              />
            </Box>
          )}
        </Stack>
      )}

      <AddBoardDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={handleAdded} />
      {toast}
    </Box>
  );
};

export default JobSourcesPage;
